import { useState, useEffect, useCallback } from "react";
import { useI18n } from "@/i18n";
import { useSessionStore } from "@/application/stores/sessionStore";
import { triggerSync, getSyncStatus, type SyncEntity, type SyncStatusType, type SyncJobRecord } from "@/infrastructure/sync/syncService";
import { colors, typography, transitions } from "@/design-system/tokens";
import { SiigoIcon } from "./SiigoIcon";

interface SyncConfig {
  entity: SyncEntity;
  statusType: SyncStatusType;
  title: string;
  subtitle: string;
  selectors?: React.ReactNode;
  getExtraParams?: () => Record<string, string>;
}

interface Props {
  configs: SyncConfig[];
  onClose: () => void;
  children?: React.ReactNode; // Optional extra tab content (e.g., carga masiva)
  extraTabLabel?: string;
}

const COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes

export function SiigoModal({ configs, onClose, children, extraTabLabel }: Props) {
  const { t } = useI18n();
  const tenantId = useSessionStore(s => s.tenantId) ?? "";
  const userId = useSessionStore(s => s.userId) ?? "";
  const [statuses, setStatuses] = useState<Record<string, SyncJobRecord | null>>({});
  const [syncing, setSyncing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"sync" | "docs">("docs");

  // Load statuses on mount
  useEffect(() => {
    configs.forEach(cfg => {
      getSyncStatus(tenantId, cfg.statusType).then(record => {
        setStatuses(prev => ({ ...prev, [cfg.entity]: record }));
      });
    });
  }, [tenantId, configs]);

  // Polling while syncing
  useEffect(() => {
    if (!syncing) return;
    const cfg = configs.find(c => c.entity === syncing);
    if (!cfg) return;
    const interval = setInterval(async () => {
      const record = await getSyncStatus(tenantId, cfg.statusType);
      setStatuses(prev => ({ ...prev, [cfg.entity]: record }));
      if (record && record.status !== "PROCESSING" && record.status !== "PENDING") {
        setSyncing(null);
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [syncing, tenantId, configs]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const handleSync = useCallback(async (cfg: SyncConfig) => {
    setSyncing(cfg.entity);
    try {
      const params: any = { tenantId, userId, syncEntity: cfg.entity };
      if (cfg.getExtraParams) Object.assign(params, cfg.getExtraParams());
      await triggerSync(params);
      // Start polling
      const record = await getSyncStatus(tenantId, cfg.statusType);
      setStatuses(prev => ({ ...prev, [cfg.entity]: record }));
    } catch {
      setToast("Error al sincronizar");
      setSyncing(null);
    }
  }, [tenantId, userId]);

  const isCooldown = (entity: string): boolean => {
    const record = statuses[entity];
    if (!record?.finishedAt) return false;
    const elapsed = Date.now() - new Date(record.finishedAt).getTime();
    return elapsed < COOLDOWN_MS;
  };

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }); } catch { return dateStr; }
  };

  const hasTabs = !!children && !!extraTabLabel;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: hasTabs ? "960px" : "640px", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #f2f2f2", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <SiigoIcon size={22} />
            <div>
              <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>Siigo</h2>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{t.erpDocuments?.title ?? "Gestión de integración ERP"}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: "4px" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Tabs if needed */}
        {hasTabs && (
          <div style={{ display: "flex", gap: "0", borderBottom: "1px solid #f2f2f2" }}>
            <button onClick={() => setActiveTab("docs")} style={{ flex: 1, fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: activeTab === "docs" ? typography.weight.bold : typography.weight.medium, color: activeTab === "docs" ? "#111" : "#888", background: "none", border: "none", borderBottom: activeTab === "docs" ? "2px solid #111" : "2px solid transparent", padding: "12px 20px", cursor: "pointer" }}>
              {extraTabLabel}
            </button>
            <button onClick={() => setActiveTab("sync")} style={{ flex: 1, fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: activeTab === "sync" ? typography.weight.bold : typography.weight.medium, color: activeTab === "sync" ? "#111" : "#888", background: "none", border: "none", borderBottom: activeTab === "sync" ? "2px solid #111" : "2px solid transparent", padding: "12px 20px", cursor: "pointer" }}>
              Sincronizar
            </button>
          </div>
        )}

        {/* Content */}
        {(!hasTabs || activeTab === "sync") ? (
          <div style={{ flex: 1, overflow: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {configs.map((cfg, i) => {
              const record = statuses[cfg.entity];
              const isProcessing = syncing === cfg.entity || record?.status === "PROCESSING";
              const isCompleted = record?.status === "COMPLETED";
              const isFailed = record?.status === "FAILED";
              const cooldown = isCooldown(cfg.entity);
              const disabled = isProcessing || cooldown;

              return (
                <div key={cfg.entity} style={{ border: "1px solid #f0f0f0", borderRadius: "14px", padding: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ width: "24px", height: "24px", borderRadius: "50%", background: isCompleted ? "rgba(34,197,94,0.1)" : "#f5f5f5", color: isCompleted ? colors.success : "#888", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                      <div>
                        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{cfg.title}</span>
                        {record && (
                          <span style={{ marginLeft: "8px", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: isCompleted ? colors.success : isFailed ? colors.error : isProcessing ? colors.warning : "#888" }}>
                            ● {record.status === "COMPLETED" ? "Completado" : record.status === "PROCESSING" ? "Procesando..." : record.status === "FAILED" ? "Error" : "Pendiente"}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleSync(cfg)}
                      disabled={disabled}
                      style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: "#fff", background: disabled ? "#ccc" : "#111", border: "none", borderRadius: "8px", padding: "8px 16px", cursor: disabled ? "not-allowed" : "pointer", transition: `background ${transitions.fast}` }}
                    >
                      {isProcessing ? "..." : "Sincronizar"}
                    </button>
                  </div>
                  <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{cfg.subtitle}</span>
                  {cfg.selectors && <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>{cfg.selectors}</div>}
                  {record && record.finishedAt && (
                    <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#bbb" }}>
                      Última sync: {formatDate(record.finishedAt)} — Creados: {record.created} | Omitidos: {record.skipped} | Total: {record.totalRows}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ flex: 1, overflow: "auto" }}>
            {children}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "16px 28px", borderTop: "1px solid #f2f2f2", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>
            Cerrar
          </button>
        </div>

        {toast && <div style={{ position: "absolute", bottom: "80px", left: "50%", transform: "translateX(-50%)", background: colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "10px 18px", borderRadius: "8px", zIndex: 10 }}>{toast}</div>}
      </div>
    </div>
  );
}
